import { useState } from 'react';
import { twMerge } from '@belong/common';
import { IconPlay } from '@belong/icons';
import { ButtonBase, Image, Text } from '@belong/ui';
import type {
  ImprovementsProgressItemModel,
  MaintenanceMediaModel,
} from 'api/models';
import Carousel from 'components/v2/Carousel/Carousel';
import ReactPlayer from 'react-player/lazy';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import { getDisplayableMedia, groupMediaByStage } from '../../utils';
import { RepairPlaceholder } from '../repair-placeholder/repair-placeholder';

type Props = {
  media: ImprovementsProgressItemModel['media'];
};

type StageKey = 'before' | 'during' | 'after';

// Muted body colour for Before, blue for During, brand green for After -
// matches the design's colour-differentiated stage labels.
const STAGE_LABEL_CLASSNAMES: Record<StageKey, string> = {
  before: 'text-dark-gray',
  during: 'text-blue',
  after: 'text-green',
};

const STAGE_LABELS: Record<StageKey, string> = {
  before: POST_INSPECTION_FLOW_STRINGS['progress.stage.before'],
  during: POST_INSPECTION_FLOW_STRINGS['progress.stage.during'],
  after: POST_INSPECTION_FLOW_STRINGS['progress.stage.after'],
};

// Hero slot only - a full player with its own focusable controls. Never put
// this inside a <ButtonBase>: nested `<video controls>` inside a `<button>`
// is invalid content and the two controls fight over the same click.
function MediaAsset({ item }: { item: MaintenanceMediaModel }) {
  if (!item.url) return null;

  if (item.mediaType === 'Video') {
    return (
      <ReactPlayer
        url={[{ src: item.url, type: 'video/mp4' }]}
        muted
        controls
        width="100%"
        height="100%"
      />
    );
  }

  return <Image alt="" src={item.url} className="object-cover" />;
}

// Thumbnail-strip slot. A static tile, not a player: react-player/lazy only
// code-splits the player library, it does not defer each instance's media
// request, so mounting a <ReactPlayer> per thumbnail would fire a
// preload/metadata request per video, unbounded by item count, for a
// control that is illegible at 32px anyway.
function MediaThumbnailAsset({ item }: { item: MaintenanceMediaModel }) {
  if (!item.url) return null;

  if (item.mediaType === 'Video') {
    return (
      <div className="flex h-full w-full items-center justify-center bg-dark-navy">
        <IconPlay width={12} height={12} className="text-white" />
      </div>
    );
  }

  return <Image alt="" src={item.url} className="object-cover" />;
}

function MediaStageColumn({
  stage,
  items,
}: {
  stage: StageKey;
  items: MaintenanceMediaModel[];
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const mainItem = items[selectedIndex] ?? items[0];

  return (
    <div className="min-w-0 flex-1">
      <Text
        variant="body"
        fontWeight="semibold"
        className={STAGE_LABEL_CLASSNAMES[stage]}
      >
        {STAGE_LABELS[stage]}
      </Text>

      <div className="mt-2xs aspect-h-1 aspect-w-1 w-full">
        <div className="overflow-hidden rounded-small border border-solid border-gray">
          {mainItem ? (
            <MediaAsset item={mainItem} />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <RepairPlaceholder
                size={60}
                className={stage === 'before' ? 'grayscale' : undefined}
              />
            </div>
          )}
        </div>
      </div>

      {items.length > 1 && (
        <div className="mt-2xs">
          <Carousel
            width={32}
            height={32}
            cellSpacing={8}
            hideOnMobile={false}
            renderBottomCenterControls={null}
          >
            {items.map((item, index) => (
              <ButtonBase
                key={item.uniqueId}
                aria-label={`${STAGE_LABELS[stage]} ${
                  item.mediaType === 'Video' ? 'video' : 'photo'
                } ${index + 1}`}
                aria-pressed={index === selectedIndex}
                className={twMerge(
                  'block h-[32px] w-[32px] overflow-hidden rounded-small bg-gray',
                  index === selectedIndex && 'ring-2 ring-navy',
                )}
                onClick={() => setSelectedIndex(index)}
              >
                <MediaThumbnailAsset item={item} />
              </ButtonBase>
            ))}
          </Carousel>
        </div>
      )}
    </div>
  );
}

export function ProgressItemMedia({ media }: Props) {
  const grouped = groupMediaByStage(getDisplayableMedia(media));

  // The design pairs Before with whichever later stage the item reached, never
  // with both: During while the work is open, After once it is finished.
  const laterStage = grouped.during.length > 0 ? 'during' : 'after';

  const columns = (['before', laterStage] as const).map((stage) => ({
    stage,
    items: grouped[stage],
  }));

  return (
    <div className="mt-sm flex gap-sm">
      {columns.map((column) => (
        <MediaStageColumn
          key={column.stage}
          stage={column.stage}
          items={column.items}
        />
      ))}
    </div>
  );
}
